import { useEffect, useState } from 'react';
import { api } from '../api';
import type { TodayDto } from '../types';
import { DoneTaskCard, TaskCard } from '../components/TaskCard';
import { Empty, ErrorBanner, Loader } from '../components/common';

interface Props {
  spaceId: string;
  onOpenPlant: (plantId: string) => void;
}

export function Today({ spaceId, onOpenPlant }: Props) {
  const [data, setData] = useState<TodayDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    try {
      setData(await api.today(spaceId));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить задачи');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setLoading(true);
    void load();
  }, [spaceId]);

  if (loading) return <Loader />;
  if (error) return <ErrorBanner message={error} />;
  if (!data) return null;

  const left = data.overdue.length + data.today.length;
  const nothing = !left && !data.upcoming.length && !data.done.length;

  return (
    <div>
      <h1>Сегодня</h1>
      <div className="hint" style={{ marginBottom: 12 }}>
        {data.plantCount} растений · {data.attentionCount} требуют внимания
        {left || data.done.length ? ` · осталось ${left}, готово ${data.done.length}` : ''}
      </div>

      {!left && data.done.length ? (
        <Empty title="🎉 На сегодня всё сделано" hint="Отмеченные задачи — ниже, в «Готово»." />
      ) : null}

      {nothing ? (
        <Empty title="На сегодня задач нет" hint="Растения в порядке. Загляните позже." />
      ) : null}

      {data.overdue.length ? (
        <>
          <h2>Просрочено</h2>
          {data.overdue.map((task) => (
            <TaskCard key={task.id} task={task} onChanged={load} onOpenPlant={onOpenPlant} />
          ))}
        </>
      ) : null}

      {data.today.length ? (
        <>
          <h2>Сегодня</h2>
          {data.today.map((task) => (
            <TaskCard key={task.id} task={task} onChanged={load} onOpenPlant={onOpenPlant} />
          ))}
        </>
      ) : null}

      {data.upcoming.length ? (
        <>
          <h2>Ближайшие</h2>
          {data.upcoming.map((task) => (
            <TaskCard key={task.id} task={task} onChanged={load} onOpenPlant={onOpenPlant} />
          ))}
        </>
      ) : null}

      {data.done.length ? (
        <>
          <h2>Готово</h2>
          {data.done.map((task) => (
            <DoneTaskCard key={task.id} task={task} onOpenPlant={onOpenPlant} />
          ))}
        </>
      ) : null}
    </div>
  );
}
